/**
 * Iter. 406.y — Medical Shield card on the Dashboard.
 *
 * Displays a reassuring "🛡️ Bouclier médical actif" card whenever the user
 * has populated `users.injuries_description` (via the onboarding wizard or
 * the J42 reassess). Lets them update or clear the description inline via
 * `PATCH /api/users/me/injuries-description` without re-triggering a full
 * plan generation.
 *
 * Why this matters:
 *   • Trust signal — the user SEES that YUMIX remembers their condition.
 *   • Easy update — if they heal or get re-injured, they don't need to wait
 *     for the next J42 cycle to refresh their profile.
 *   • Engagement — injured profiles historically churn the most; the visual
 *     confirmation reduces anxiety and lifts retention.
 */
import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Pencil, Loader2, X, Check, HelpCircle, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage, api, useAuth } from '../context/AppContext';

const MAX_LEN = 1200;
const CHECKIN_DAYS_THRESHOLD = 90;

export default function MedicalShieldCard() {
  const { user, updateUser } = useAuth();
  const { language, t } = useLanguage();
  const initial = (user?.injuries_description || '').trim();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  // Iter. 406.z — Proactive check-in flow. When the medical description is
  // older than CHECKIN_DAYS_THRESHOLD days, surface a friendly prompt asking
  // the user whether the condition is still active or if they've healed.
  // `checkinAction` carries the in-flight button state ("renew" or "heal")
  // so we can disable both during the PATCH round-trip.
  const [checkinAction, setCheckinAction] = useState(null);
  const [checkinDismissed, setCheckinDismissed] = useState(false);
  // Lazy-init lets us call Date.now() ONCE at mount without violating the
  // react-hooks/purity rule (the function passed to useState is only invoked
  // on the first render).
  const [mountedAtMs] = useState(() => Date.now());

  const updatedAt = user?.injuries_description_updated_at;
  // Iter. 406.z — Days since the description was last touched. Drives the
  // check-in prompt visibility (>= 90 days). Memo keeps the value stable
  // across renders so toggling editing state doesn't bounce the prompt.
  const daysSinceUpdate = useMemo(() => {
    if (!updatedAt) return null;
    return Math.floor((mountedAtMs - new Date(updatedAt).getTime()) / 86400000);
  }, [updatedAt, mountedAtMs]);

  // No description → no card. The card is reassurance, not a recruitment ad.
  if (!initial && !editing) return null;

  const updatedLabel = updatedAt ? new Date(updatedAt).toLocaleDateString(
    language === 'fr' ? 'fr-CA' : 'en-CA',
    { day: 'numeric', month: 'short', year: 'numeric' },
  ) : null;

  const showCheckin = !editing && !checkinDismissed
    && initial && daysSinceUpdate !== null
    && daysSinceUpdate >= CHECKIN_DAYS_THRESHOLD;

  // Generic PATCH helper used by both the save() flow and the check-in
  // shortcuts (renew / heal). Always syncs the AuthContext on success.
  const patchInjury = async (next) => {
    const res = await api.patch('/users/me/injuries-description', {
      injuries_description: next || null,
    });
    if (updateUser) {
      updateUser({
        ...user,
        injuries_description: res.data?.injuries_description ?? (next || null),
        injuries_description_updated_at: new Date().toISOString(),
      });
    }
    return res;
  };

  const renewCheckin = async () => {
    setCheckinAction('renew');
    try {
      // PATCH with the EXACT same text → server stamps a fresh
      // `injuries_description_updated_at`, the check-in disappears for another
      // 90 days. No content change, no plan re-generation.
      await patchInjury(initial);
      toast.success(
        t('🛡️ Bouclier confirmé. On revérifie dans 90 jours.',
          '🛡️ Shield confirmed. We\'ll check again in 90 days.'),
        { duration: 4500 },
      );
    } catch (err) {
      toast.error(t('Mise à jour impossible.', 'Update failed.'));
    } finally {
      setCheckinAction(null);
    }
  };

  const healCheckin = async () => {
    setCheckinAction('heal');
    try {
      await patchInjury('');
      toast.success(
        t('🎉 Bravo ! Bouclier retiré, l\'IA libère les exercices restreints.',
          '🎉 Awesome! Shield cleared, the AI unlocks restricted exercises.'),
        { duration: 5000 },
      );
    } catch (err) {
      toast.error(t('Mise à jour impossible.', 'Update failed.'));
    } finally {
      setCheckinAction(null);
    }
  };

  const save = async () => {
    const next = (draft || '').trim();
    if (next === initial) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await patchInjury(next);
      toast.success(next
        ? t('🛡️ Bouclier médical mis à jour.', '🛡️ Medical shield updated.')
        : t('🛡️ Bouclier médical retiré — bonne guérison !', '🛡️ Medical shield cleared — heal well!'),
        { duration: 4000 },
      );
      setEditing(false);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(typeof detail === 'string' ? detail : (
        language === 'fr' ? 'Mise à jour impossible.' : 'Update failed.'
      ));
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(initial);
    setEditing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25 }}
      className="rounded-2xl border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-500/12 via-teal-500/6 to-transparent p-4 space-y-3"
      data-testid="medical-shield-card"
    >
      <div className="flex items-start gap-2.5">
        <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-heading uppercase tracking-wider text-emerald-200 leading-tight">
            {t('Bouclier médical actif', 'Medical shield active')}
          </h3>
          <p className="text-[10px] text-emerald-300/70 mt-0.5">
            {t(
              "L'IA adapte chaque exercice pour protéger les zones décrites.",
              "The AI adapts every exercise to protect the described zones.",
            )}
          </p>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => { setDraft(initial); setEditing(true); }}
            className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-200 hover:text-white px-2 py-1 rounded-md border border-emerald-500/40 hover:border-emerald-400 transition-colors"
            data-testid="medical-shield-edit-btn"
          >
            <Pencil className="w-3 h-3" />
            {t('Mettre à jour', 'Update')}
          </button>
        )}
      </div>

      <AnimatePresence initial={false} mode="wait">
        {editing ? (
          <motion.div
            key="editor"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-2 overflow-hidden"
          >
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={5}
              maxLength={MAX_LEN}
              autoFocus
              placeholder={t(
                "Ex: Hernie discale lombaire, tendinite à l'épaule droite, douleur aux genoux en faisant des squats, etc. (Laissez vide si guéri).",
                'E.g. lumbar disc herniation, right shoulder tendinitis, knee pain on squats, etc. (Leave empty if healed).',
              )}
              className="w-full rounded-lg bg-secondary/50 border border-emerald-500/30 text-sm leading-relaxed px-3 py-2 focus:border-emerald-400 focus:outline-none placeholder:text-muted-foreground/40"
              data-testid="medical-shield-textarea"
            />
            <div className="flex items-center justify-between text-[10px]">
              <span className="text-muted-foreground/70">
                {draft.trim().length === 0
                  ? t('Vide → bouclier retiré', 'Empty → shield removed')
                  : t('🛡️ Bouclier conservé', '🛡️ Shield kept')}
              </span>
              <span className="text-muted-foreground tabular-nums">{draft.length}/{MAX_LEN}</span>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={cancel}
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-border hover:bg-secondary text-xs font-semibold transition-colors disabled:opacity-50"
                data-testid="medical-shield-cancel-btn"
              >
                <X className="w-3.5 h-3.5" />
                {t('Annuler', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                data-testid="medical-shield-save-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {t('Enregistrer', 'Save')}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="readonly"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-2"
          >
            <div
              className="rounded-lg bg-black/20 border border-emerald-500/15 px-3 py-2 text-xs leading-relaxed text-foreground/90 whitespace-pre-wrap break-words"
              data-testid="medical-shield-description"
            >
              {initial}
            </div>
            {updatedLabel && (
              <p className="text-[10px] text-emerald-300/60 text-right">
                {t('Mis à jour le', 'Updated on')} {updatedLabel}
              </p>
            )}

            {/* 🩺 Iter. 406.z — Proactive medical check-in (≥90 days stale).
                Friendly nudge to keep the medical context up to date without
                forcing the user to remember to do it. Dismissible per-session
                so it doesn't nag if the user wants to handle it later. */}
            {showCheckin && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-2 rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 space-y-2.5"
                data-testid="medical-shield-checkin"
              >
                <div className="flex items-start gap-2">
                  <HelpCircle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs leading-snug text-amber-100">
                      {t(
                        `On a noté ces blessures il y a ${daysSinceUpdate} jours. Toujours d'actualité ?`,
                        `We logged this ${daysSinceUpdate} days ago. Still accurate?`,
                      )}
                    </p>
                    <p className="text-[10px] text-amber-200/70 mt-0.5">
                      {t(
                        'Ta réponse permet à l\'IA de garder ton plan parfaitement adapté.',
                        'Your answer keeps the AI plan perfectly tailored.',
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCheckinDismissed(true)}
                    disabled={!!checkinAction}
                    className="shrink-0 p-0.5 rounded text-amber-300/60 hover:text-amber-100 transition-colors disabled:opacity-40"
                    aria-label={t('Reporter', 'Dismiss')}
                    data-testid="medical-shield-checkin-dismiss"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={renewCheckin}
                    disabled={!!checkinAction}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-[11px] font-semibold text-amber-100 transition-colors disabled:opacity-50"
                    data-testid="medical-shield-checkin-renew"
                  >
                    {checkinAction === 'renew'
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <ShieldCheck className="w-3.5 h-3.5" />}
                    {t('Toujours actif', 'Still active')}
                  </button>
                  <button
                    type="button"
                    onClick={healCheckin}
                    disabled={!!checkinAction}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 text-[11px] font-semibold text-emerald-100 transition-colors disabled:opacity-50"
                    data-testid="medical-shield-checkin-heal"
                  >
                    {checkinAction === 'heal'
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <PartyPopper className="w-3.5 h-3.5" />}
                    {t('Je suis guéri 🎉', 'I\'m healed 🎉')}
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
